import { useState } from 'react';
import {
  RED_FLAG_SYMPTOMS,
  hasRedFlag,
  type Onset,
  type Severity,
} from '@rx/shared';

/**
 * "I don't feel right."
 *
 * The time is captured automatically; onset is a coarse choice because asking someone
 * who feels unwell to pick a clock time is asking too much. Everything is a chip —
 * free text is optional and last.
 */

/** Ordinary impairment signs, from the precautions document. */
const SYMPTOM_CHIPS: { key: string; label: string }[] = [
  { key: 'EXCESSIVE_SLEEPINESS', label: 'Unusually sleepy' },
  { key: 'SLEPT_MUCH_MORE_THAN_USUAL', label: 'Slept much more than usual' },
  { key: 'FATIGUE', label: 'Tired' },
  { key: 'DIZZY', label: 'Dizzy' },
  { key: 'LIGHTHEADED', label: 'Lightheaded' },
  { key: 'WEAK', label: 'Weak' },
  { key: 'UNSTEADY', label: 'Unsteady' },
  { key: 'SLOWED_THINKING', label: 'Slow thinking' },
  { key: 'DISORIENTED', label: 'Confused' },
  { key: 'SHORT_TERM_MEMORY_DIFFICULTY', label: 'Memory trouble' },
  { key: 'FAINTNESS', label: 'Faint' },
  { key: 'NAUSEATED', label: 'Nauseated' },
  { key: 'VOMITING', label: 'Vomiting' },
  { key: 'HEADACHE', label: 'Headache' },
  { key: 'HEART_RACING', label: 'Heart racing' },
  { key: 'HEART_SLOW', label: 'Heart very slow' },
  { key: 'OTHER', label: 'Something else' },
];

/**
 * The emergency list, verbatim in meaning from the precautions document: these mean
 * "treat it as a possible medical emergency rather than assuming it is medication."
 * Kept as their own visually distinct group so they are never mixed in with feeling
 * a bit tired.
 */
const RED_FLAG_LABELS: Record<string, string> = {
  SLURRED_SPEECH: 'Slurred speech',
  CANNOT_WALK: "Can't walk normally",
  CANNOT_WALK_NORMALLY: "Can't walk normally",
  MARKED_CONFUSION: 'Very confused',
  ONE_SIDED_WEAKNESS: 'Weak on one side',
  FACIAL_DROOPING: 'Face drooping',
  FAINTING: 'Fainted',
  SEVERE_DIFFICULTY_STAYING_AWAKE: "Can't stay awake",
  CHEST_PAIN: 'Chest pain',
  TROUBLE_BREATHING: 'Trouble breathing',
  SEIZURE: 'Seizure',
  UNAROUSABLE: "Can't be woken",
};

const ONSET_CHOICES: { key: Onset; label: string }[] = [
  { key: 'NOW', label: 'Right now' },
  { key: 'WITHIN_LAST_HOUR', label: 'In the last hour' },
  { key: 'EARLIER_TODAY', label: 'Earlier today' },
  { key: 'LAST_NIGHT', label: 'Last night' },
];

const SEVERITY_CHOICES: { key: Severity; label: string }[] = [
  { key: 'MILD', label: 'Mild' },
  { key: 'MODERATE', label: 'Moderate' },
  { key: 'BAD', label: 'Bad' },
];

export interface SymptomSubmission {
  symptoms: string[];
  severity?: Severity;
  onset: Onset;
  notes?: string;
}

export function SymptomFlow({
  caregiverPhone,
  onSubmit,
  onCancel,
}: {
  caregiverPhone?: string;
  onSubmit: (s: SymptomSubmission) => Promise<void>;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [severity, setSeverity] = useState<Severity | undefined>();
  const [onset, setOnset] = useState<Onset>('NOW');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [emergencyShown, setEmergencyShown] = useState(false);

  const redFlagged = hasRedFlag(selected);

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // Surface the emergency panel the moment a red flag is chosen, before anything
      // else is asked. Saving still happens either way — see below.
      if (!prev.includes(key) && hasRedFlag([key])) setEmergencyShown(true);
      return next;
    });
  }

  async function save(): Promise<void> {
    if (selected.length === 0) return;
    setBusy(true);
    const submission: SymptomSubmission = { symptoms: selected, onset };
    if (severity !== undefined) submission.severity = severity;
    if (notes.trim().length > 0) submission.notes = notes.trim();
    try {
      await onSubmit(submission);
    } finally {
      setBusy(false);
    }
  }

  /*
   * The emergency panel.
   *
   * It interrupts, but it never holds the log hostage: "Save this and go back" records
   * the symptom exactly as chosen. The precautions document is explicit that these
   * signs should not be assumed to be medication, so the first thing offered is 911 —
   * not a form.
   */
  if (emergencyShown && redFlagged) {
    return (
      <main className="app">
        <section className="emergency">
          <h1 className="emergency-title">This can be an emergency</h1>
          <p className="emergency-body">
            Do not assume this is the medication. If this is new, call 911.
          </p>
          <a className="emergency-call" href="tel:911">
            Call 911
          </a>
          {caregiverPhone !== undefined && (
            <a className="emergency-second" href={`tel:${caregiverPhone}`}>
              Call Rob
            </a>
          )}
          <button
            className="btn-quiet emergency-dismiss"
            type="button"
            disabled={busy}
            onClick={() => {
              void save();
            }}
          >
            {busy ? 'Saving…' : 'Save this and go back'}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="app">
      <h1>How do you feel?</h1>
      <p className="muted">Tap everything that applies. The time is recorded for you.</p>

      <div className="chips">
        {SYMPTOM_CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`chip ${selected.includes(c.key) ? 'on' : ''}`}
            aria-pressed={selected.includes(c.key)}
            onClick={() => {
              toggle(c.key);
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      <h2>Tell someone straight away</h2>
      <p className="muted" style={{ marginTop: '-0.3rem' }}>
        These can be an emergency, not a side effect.
      </p>
      <div className="chips">
        {RED_FLAG_SYMPTOMS.map((key) => (
          <button
            key={key}
            type="button"
            className={`chip red ${selected.includes(key) ? 'on' : ''}`}
            aria-pressed={selected.includes(key)}
            onClick={() => {
              toggle(key);
            }}
          >
            {RED_FLAG_LABELS[key] ?? key}
          </button>
        ))}
      </div>

      <h2>When did it start?</h2>
      <div className="chips">
        {ONSET_CHOICES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`chip ${onset === c.key ? 'on' : ''}`}
            aria-pressed={onset === c.key}
            onClick={() => {
              setOnset(c.key);
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      <h2>How bad is it?</h2>
      <div className="chips">
        {SEVERITY_CHOICES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`chip wide ${severity === c.key ? 'on' : ''}`}
            aria-pressed={severity === c.key}
            onClick={() => {
              setSeverity(c.key);
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      <h2>Anything else? (optional)</h2>
      <textarea
        className="field notes"
        rows={3}
        aria-label="Anything else"
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
        }}
      />

      <div className="btn-row">
        <button className="btn-primary" disabled={busy || selected.length === 0} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </main>
  );
}
